import {
	disposeViewerEntry,
	loadAllSpreadingCompactions,
	syncViewerWithEntry,
	teardownViewerState,
	trackSpreadingCompactionJob,
} from "@/components/Project/spreading-compaction/spreadingCompactionService";
import type { ActionContext } from "vuex";
import type {
	IExecution,
	ISpreadingCompaction,
	ISpreadingCompactionDraft,
	ISpreadingCompactionState,
} from "./spreading-compaction-types";

function findById(
	state: ISpreadingCompactionState,
	id: number,
): ISpreadingCompaction | undefined {
	return state.spreadingCompactions.find((e) => e.id === id);
}

function findSelected(
	state: ISpreadingCompactionState,
): ISpreadingCompaction | undefined {
	if (!state.selectedId) return undefined;
	return findById(state, state.selectedId);
}

const state: ISpreadingCompactionState = {
	spreadingCompactions: [],
	draft: null,
	selectedId: null,
	isActive: false,
	isExecuting: false,
	draftError: null,
};

// --- Mutations ---

const mutations = {
	ADD(state: ISpreadingCompactionState, item: ISpreadingCompaction) {
		state.spreadingCompactions.push(item);
	},

	CLEAR(state: ISpreadingCompactionState) {
		state.spreadingCompactions = [];
		state.selectedId = null;
		state.draft = null;
	},

	REMOVE(state: ISpreadingCompactionState, id: number) {
		state.spreadingCompactions = state.spreadingCompactions.filter(
			(e) => e.id !== id,
		);
		if (state.selectedId === id) {
			state.selectedId = state.spreadingCompactions[0]?.id ?? null;
		}
	},

	SELECT(state: ISpreadingCompactionState, id: number | null) {
		state.selectedId = id;
	},

	SET_DRAFT(
		state: ISpreadingCompactionState,
		draft: ISpreadingCompactionDraft | null,
	) {
		state.draft = draft;
		state.draftError = null;
	},

	SET_DRAFT_ERROR(state: ISpreadingCompactionState, error: string | null) {
		state.draftError = error;
	},

	UPDATE_DRAFT(
		state: ISpreadingCompactionState,
		payload: Partial<ISpreadingCompactionDraft>,
	) {
		if (!state.draft) return;
		if (payload.name !== undefined) state.draft.name = payload.name;
		if (payload.target !== undefined) state.draft.target = payload.target;
		if (payload.terrain !== undefined) state.draft.terrain = payload.terrain;
		if (payload.compactionSurfaceSpacing !== undefined) {
			state.draft.compactionSurfaceSpacing = payload.compactionSurfaceSpacing;
		}
		if (payload.slopeShoulderOffset !== undefined) {
			state.draft.slopeShoulderOffset = payload.slopeShoulderOffset;
		}
		if (payload.layerMode !== undefined) {
			state.draft.layerMode = payload.layerMode;
		}
		if (payload.targetVisible !== undefined) {
			state.draft.targetVisible = payload.targetVisible;
		}
		if (payload.targetOpacity !== undefined) {
			state.draft.targetOpacity = payload.targetOpacity;
		}
	},

	SET_ACTIVE(state: ISpreadingCompactionState, active: boolean) {
		state.isActive = active;
	},

	SET_EXECUTING(state: ISpreadingCompactionState, executing: boolean) {
		state.isExecuting = executing;
	},

	UPDATE(
		state: ISpreadingCompactionState,
		payload: { id: number } & Partial<
			Pick<
				ISpreadingCompaction,
				| "name"
				| "target"
				| "terrain"
				| "compactionSurfaceSpacing"
				| "slopeShoulderOffset"
				| "layerMode"
			>
		>,
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (!spreadingCompaction) return;
		if (payload.name !== undefined) spreadingCompaction.name = payload.name;
		if (payload.target !== undefined)
			spreadingCompaction.target = payload.target;
		if (payload.terrain !== undefined)
			spreadingCompaction.terrain = payload.terrain;
		if (payload.compactionSurfaceSpacing !== undefined) {
			spreadingCompaction.compactionSurfaceSpacing =
				payload.compactionSurfaceSpacing;
		}
		if (payload.slopeShoulderOffset !== undefined) {
			spreadingCompaction.slopeShoulderOffset = payload.slopeShoulderOffset;
		}
		if (payload.layerMode !== undefined) {
			spreadingCompaction.layerMode = payload.layerMode;
		}
	},

	SET_EXECUTION(
		state: ISpreadingCompactionState,
		payload: { id: number; execution: IExecution },
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (!spreadingCompaction) return;
		spreadingCompaction.execution = payload.execution;
	},

	SET_TARGET_VISIBLE(
		state: ISpreadingCompactionState,
		payload: { id: number; visible: boolean },
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (spreadingCompaction)
			spreadingCompaction.targetVisible = payload.visible;
	},

	SET_TARGET_OPACITY(
		state: ISpreadingCompactionState,
		payload: { id: number; opacity: number },
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (spreadingCompaction)
			spreadingCompaction.targetOpacity = payload.opacity;
	},

	SET_VISIBLE(
		state: ISpreadingCompactionState,
		payload: { id: number; visible: boolean },
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (spreadingCompaction) spreadingCompaction.visible = payload.visible;
	},

	SET_OFFSET_VISIBLE(
		state: ISpreadingCompactionState,
		payload: {
			id: number;
			type: "spreading" | "compaction";
			offset: number;
			visible: boolean;
		},
	) {
		const spreadingCompaction = findById(state, payload.id);
		if (!spreadingCompaction) return;
		const existing = spreadingCompaction.offsetVisibility.find(
			(o) => o.type === payload.type && o.offset === payload.offset,
		);
		if (existing) {
			existing.visible = payload.visible;
		} else {
			spreadingCompaction.offsetVisibility.push({
				type: payload.type,
				offset: payload.offset,
				visible: payload.visible,
			});
		}
	},
};
const getters = {
	spreadingCompactions: (state: ISpreadingCompactionState) =>
		state.spreadingCompactions,

	selected: (state: ISpreadingCompactionState) => findSelected(state),

	draft: (state: ISpreadingCompactionState) => state.draft,

	selectedId: (state: ISpreadingCompactionState) => state.selectedId,

	isActive: (state: ISpreadingCompactionState) => state.isActive,

	isProcessing: (state: ISpreadingCompactionState) => {
		if (state.isExecuting) return true;
		const spreadingCompaction = findSelected(state);
		return spreadingCompaction?.execution.status === "processing";
	},

	executionError: (state: ISpreadingCompactionState) => {
		// A failed draft has no entry to hold the status, so it reports separately.
		if (state.draftError) return state.draftError;
		const spreadingCompaction = findSelected(state);
		return spreadingCompaction?.execution.status === "error"
			? spreadingCompaction.execution.error
			: null;
	},

	hasResult: (state: ISpreadingCompactionState) => {
		const spreadingCompaction = findSelected(state);
		return spreadingCompaction?.execution.status === "success";
	},

	resultSurfaces: (state: ISpreadingCompactionState) => {
		const spreadingCompaction = findSelected(state);
		return spreadingCompaction?.execution.status === "success"
			? spreadingCompaction.execution.result.surfaces
			: [];
	},
};

// --- Actions ---

const actions = {
	/**
	 * Project-startup: dispose any existing viewer entries, GET persisted records,
	 * ADD them to store, then sync viewer for each.
	 */
	async loadAndRestore(
		ctx: ActionContext<ISpreadingCompactionState, unknown>,
	): Promise<void> {
		const rootState = ctx.rootState as Record<string, unknown>;
		// dispose viewer state for whatever is currently in store
		for (const record of ctx.state.spreadingCompactions) {
			try {
				teardownViewerState(record, rootState);
				disposeViewerEntry(record.id, rootState);
			} catch (e: unknown) {
				console.warn("[spreading-compaction] startup clear failed:", e);
			}
		}
		ctx.commit("CLEAR");

		try {
			const records = await loadAllSpreadingCompactions(rootState);
			for (const record of records) {
				ctx.commit("ADD", record);
				await syncViewerWithEntry(record, rootState);
			}
		} catch (e: unknown) {
			console.warn("[spreading-compaction] startup load failed:", e);
		}

		resumeActiveJobs(ctx);
	},
};

/**
 * Entries the server listed as still running (e.g. page reloaded
 * mid-calculation) arrive as "processing" and are polled to completion —
 * same idea as resuming an asset upload's progress.
 */
function resumeActiveJobs(
	ctx: ActionContext<ISpreadingCompactionState, unknown>,
): void {
	const tracker = {
		commit: ctx.commit,
		getEntry: (id: number) => findById(ctx.state, id),
		rootState: ctx.rootState as Record<string, unknown>,
	};
	for (const entry of ctx.state.spreadingCompactions) {
		if (entry.execution.status !== "processing") continue;
		// Not awaited: each entry polls independently; outcome lands on it.
		void trackSpreadingCompactionJob(entry.id, tracker);
	}
}

export default {
	namespaced: true,
	state,
	mutations,
	getters,
	actions,
};
